import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { XCircle, ArrowLeft } from 'lucide-react';

interface AccessDeniedProps {
  title?: string;
  message?: string;
  showBackButton?: boolean;
  backTo?: string;
  backButtonText?: string;
  className?: string;
}

export const AccessDenied: React.FC<AccessDeniedProps> = ({
  title,
  message,
  showBackButton = true,
  backTo = '/',
  backButtonText,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <div className={`flex items-center justify-center min-h-screen ${className}`}>
      <div className='text-center max-w-md px-4'>
        <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
          <XCircle className='text-red-500' size={32} />
        </div>
        <h1 className='text-2xl font-bold text-gray-900 mb-2'>
          {title || t('errors.accessDenied.title')}
        </h1>
        <p className='text-gray-600 mb-6'>{message || t('errors.accessDenied.message')}</p>
        {showBackButton && (
          <Link
            to={backTo}
            className='inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
          >
            <ArrowLeft size={18} />
            <span>{backButtonText || t('common.back')}</span>
          </Link>
        )}
      </div>
    </div>
  );
};

interface KnowledgeBaseAccessDeniedProps {
  isPrivate?: boolean;
  className?: string;
}

export const KnowledgeBaseAccessDenied: React.FC<KnowledgeBaseAccessDeniedProps> = ({
  isPrivate = false,
  className = '',
}) => {
  const { t } = useTranslation();

  return (
    <div className={`flex items-center justify-center min-h-screen ${className}`}>
      <div className='text-center max-w-md px-4'>
        <div className='w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4'>
          <XCircle className='text-red-500' size={32} />
        </div>
        <h1 className='text-2xl font-bold text-gray-900 mb-2'>
          {t('errors.accessDenied.knowledgeBase.title')}
        </h1>
        <p className='text-gray-600 mb-6'>
          {isPrivate
            ? t('errors.accessDenied.knowledgeBase.privateMessage')
            : t('errors.accessDenied.knowledgeBase.message')}
        </p>
        <div className='flex flex-col sm:flex-row items-center justify-center gap-3'>
          <Link
            to='/knowledge'
            className='inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors'
          >
            <ArrowLeft size={18} />
            <span>{t('errors.accessDenied.knowledgeBase.backToKnowledge')}</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AccessDenied;
